import { useState } from "react";
import { NotepadTextIcon, PlusIcon } from "lucide-react";

import { CompanyNoteSheet } from "./CompanyNoteSheet";
import { CompanyNotesTable } from "./CompanyNotesTable";
import { Column, EmptyState, ErrorText, LoadingIndicator } from "@/components";
import { Button } from "@/components/ui/button";

import { useGetCompanyNotes } from "@/hooks/queries/useGetCompanyNotes";
import { cn } from "@/lib/utils";

export const CompanyNotes = ({ className }: { className?: string }) => {
  const {
    data: companyNotes = [],
    isError: isCompanyNotesError,
    isPending: isCompanyNotesPending,
  } = useGetCompanyNotes();
  const [isCompanyNoteSheetOpen, setIsCompanyNoteSheetOpen] = useState(false);

  const render = () => {
    if (isCompanyNotesPending) {
      return <LoadingIndicator variant="medium" />;
    } else if (isCompanyNotesError) {
      return (
        <Column className="h-full items-center justify-center">
          <ErrorText errorMessage="An error occurred while loading company notes. Please let the engineers know if they are not already on it!" />
        </Column>
      );
    } else if (companyNotes.length === 0) {
      return (
        <Column className={cn("items-end gap-6", className)}>
          <EmptyState
            description="Notes added about this company will appear here."
            handlePrimaryClick={() => {
              setIsCompanyNoteSheetOpen(true);
            }}
            Icon={NotepadTextIcon}
            primaryButtonName="Create a note"
            primaryButtonVariant="default"
            title="Add your first note"
          />
          <CompanyNoteSheet
            companyNote={null}
            onOpenChange={setIsCompanyNoteSheetOpen}
            open={isCompanyNoteSheetOpen}
          />
        </Column>
      );
    }
    return (
      <Column className={cn("items-end gap-6", className)}>
        <Button
          className="rounded-full"
          onClick={() => {
            setIsCompanyNoteSheetOpen(true);
          }}
        >
          <PlusIcon className="h-4 w-4" />
          Create note
        </Button>
        <CompanyNotesTable className="w-full" companyNotes={companyNotes} />
        <CompanyNoteSheet
          companyNote={null}
          onOpenChange={setIsCompanyNoteSheetOpen}
          open={isCompanyNoteSheetOpen}
        />
      </Column>
    );
  };

  return render();
};
